import clsx from 'clsx';
import { Fragment, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { HiArrowsUpDown } from 'react-icons/hi2';

import { useIsMobile } from '@hooks/useIsMobile.tsx';

import { Skeleton } from '../Skeleton';

import { HEADER_HEIGHT, PAGINATION_HEIGHT, rowsThatFit } from './autoFitRows';
import { EMPTY_PLACEHOLDER } from './cellPlaceholder';
import styles from './DataTable.module.scss';

export type Column<T> = {
  key: keyof T | string;
  label: ReactNode;
  sortable?: boolean;
  render?: (value: T[keyof T], row: T) => ReactNode;
  sortValue?: (row: T) => string | number;
  width?: string;
  truncate?: boolean;
  /**
   * What the column's cells show while the table is `loading`. Left out, a text bone;
   * a bone of another shape where the cell is one — an avatar, a pill — and `null`
   * for a column that has nothing to stand in for, such as the row's actions.
   */
  skeleton?: ReactNode;
};

/**
 * Server-driven pagination. When it is passed, `data` is *one page* of rows rather than
 * the whole set: the table renders what it is given, and the footer's page numbers are
 * derived from `totalCount` instead of from `data.length`.
 *
 * `onPageSizeChange` is how auto-fit survives the move to the server. The table is the
 * only thing that knows how many rows fit the viewport, so it measures as before and
 * reports the number back — the caller turns that into the `limit` it asks for.
 */
export type DataTablePagination = {
  /** 1-based, owned by the caller. */
  page: number;
  pageSize: number;
  /** Rows matching the filter across every page, as the service counts them. */
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
};

export type SortDirection = 'asc' | 'desc';

export type DataTableSortState = {
  /** A column's `key`. */
  field: string;
  direction: SortDirection;
};

/**
 * Server-driven sorting, the partner of `pagination`. A server-paged table holds one page,
 * so sorting what it holds would reorder only the rows on screen; with this the header
 * clicks are reported instead, and the caller asks the service for the list in that
 * order. `data` is rendered exactly as given.
 *
 * A column offers it only if it is marked `sortable` and its `key` is in `fields`, so the
 * one list of what the service can order by decides which headers stay plain.
 */
export type DataTableSort = {
  /** `null` is the service's default order. */
  value: DataTableSortState | null;
  onChange: (next: DataTableSortState | null) => void;
  /** The column keys the service can order by. */
  fields: readonly string[];
};

type DataTableProps<T extends { id: number | string }> = {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  itemsPerPage?: number;
  itemsPerBigPage?: number;
  autoFit?: boolean;
  renderSubRow?: (row: T) => ReactNode;
  rowClassName?: (row: T) => string | undefined;
  pagination?: DataTablePagination;
  sort?: DataTableSort;
  /**
   * The first read is still in flight: the real header, and a page of bone rows in
   * place of `data` — as many as a page of data would fill, so nothing moves when it
   * lands. Leave it off for a refetch that already has rows to show.
   */
  loading?: boolean;
};

const ITEMS_PER_PAGE = 10;
const ITEMS_PER_BIG_PAGE = 12;

// Varied so a page of bones reads as rows of text rather than as stripes.
const BONE_WIDTHS = ['70%', '50%', '60%', '40%'];

const DataTable = <T extends { id: number | string }>({
  data,
  columns,
  onRowClick,
  itemsPerPage: customItemsPerPage,
  itemsPerBigPage: customItemsPerBigPage,
  autoFit = true,
  renderSubRow,
  rowClassName,
  pagination,
  sort,
  loading = false,
}: DataTableProps<T>) => {
  const isMobile = useIsMobile({ breakpoint: 1441 });
  const [localPage, setLocalPage] = useState(1);
  const [sortField, setSortField] = useState<keyof T | string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLTableSectionElement | null>(null);
  const bodyRef = useRef<HTMLTableSectionElement | null>(null);
  const paginationRef = useRef<HTMLDivElement | null>(null);
  const [autoFitCount, setAutoFitCount] = useState<number | null>(null);

  // Read through a ref so a caller passing an inline callback does not re-subscribe
  // the observer on every render. Kept in step from an effect declared ahead of the
  // one that reads it, so the measurement never calls a stale callback.
  const onPageSizeChangeRef = useRef(pagination?.onPageSizeChange);
  useEffect(() => {
    onPageSizeChangeRef.current = pagination?.onPageSizeChange;
  });

  useEffect(() => {
    if (!autoFit) return;
    const node = containerRef.current;
    if (!node) return;

    const compute = () => {
      const headerH = headerRef.current?.clientHeight ?? HEADER_HEIGHT;
      const paginationH = paginationRef.current?.clientHeight ?? PAGINATION_HEIGHT;
      const { top } = node.getBoundingClientRect();
      const next = rowsThatFit(top, headerH, paginationH);
      setAutoFitCount(next);
      onPageSizeChangeRef.current?.(next);
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(node);
    if (headerRef.current) observer.observe(headerRef.current);
    if (paginationRef.current) observer.observe(paginationRef.current);
    window.addEventListener('resize', compute);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, [autoFit]);

  const isServerPaged = !!pagination;

  const itemsPerPage = isServerPaged
    ? pagination.pageSize
    : autoFit
      ? (autoFitCount ?? customItemsPerBigPage ?? ITEMS_PER_BIG_PAGE)
      : isMobile
        ? (customItemsPerPage ?? ITEMS_PER_PAGE)
        : (customItemsPerBigPage ?? ITEMS_PER_BIG_PAGE);

  const isServerSorted = !!sort;
  const activeSortField = isServerSorted ? (sort.value?.field ?? null) : sortField;
  const activeSortDirection = isServerSorted ? (sort.value?.direction ?? null) : sortDirection;

  /**
   * Without `sort`, sorting is client-side, over everything the table holds — which is
   * the whole set only while the table owns the pagination. Server-paged, it holds one
   * page, and a sort that silently reordered twelve of two hundred rows would be a worse
   * answer than no sort at all, so the headers offer one only for what the service sorts.
   */
  const canSortBy = (column: Column<T>) =>
    !!column.sortable &&
    (isServerSorted ? sort.fields.includes(String(column.key)) : !isServerPaged);

  const sortedData = useMemo(() => {
    if (isServerSorted || !sortField || !sortDirection) {
      return data;
    }

    const col = columns.find((c) => c.key === sortField);
    return [...data].sort((a, b) => {
      const aValue = col?.sortValue ? col.sortValue(a) : a[sortField as keyof T];
      const bValue = col?.sortValue ? col.sortValue(b) : b[sortField as keyof T];

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue ?? '');
      const bStr = String(bValue ?? '');
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [isServerSorted, data, sortField, sortDirection, columns]);

  const currentPage = isServerPaged ? pagination.page : localPage;
  const totalItems = isServerPaged ? pagination.totalCount : sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  // Only meaningful while the table owns the page: a server-paged caller owns its own
  // page number and clamps it against the count it fetched.
  if (!isServerPaged && localPage > totalPages) {
    setLocalPage(totalPages);
  }

  const paginatedData = useMemo(() => {
    // Server-paged, `data` *is* the page — slicing it again would drop rows.
    if (isServerPaged) return sortedData;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [isServerPaged, currentPage, itemsPerPage, sortedData]);

  /** Ascending → descending → off, the same cycle whoever does the sorting. */
  const handleSort = (field: keyof T | string) => {
    const nextDirection: SortDirection | null =
      activeSortField !== field ? 'asc' : activeSortDirection === 'asc' ? 'desc' : null;

    if (isServerSorted) {
      // The caller owns the page as well, and resets it along with the sort.
      sort.onChange(nextDirection && { field: String(field), direction: nextDirection });
      return;
    }

    setSortField(nextDirection && field);
    setSortDirection(nextDirection);
    setLocalPage(1);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    if (isServerPaged) {
      pagination.onPageChange(page);
      return;
    }
    setLocalPage(page);
  };

  const renderSortIcon = (field: keyof T | string) => {
    const isActive = activeSortField === field;
    return (
      <span className={clsx(styles.sortIcon, { [styles.active]: isActive })} aria-hidden="true">
        {isActive && activeSortDirection === 'asc' && '▲'}
        {isActive && activeSortDirection === 'desc' && '▼'}
        {!isActive && <HiArrowsUpDown />}
      </span>
    );
  };

  const ariaSortOf = (field: keyof T | string) => {
    if (activeSortField !== field || !activeSortDirection) return undefined;
    return activeSortDirection === 'asc' ? 'ascending' : 'descending';
  };

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4);
      } else if (currentPage >= totalPages - 2) {
        pages.push(totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(currentPage - 1, currentPage, currentPage + 1);
      }
    }

    return pages;
  };

  const getCellValue = (row: T, column: Column<T>): ReactNode => {
    const value = row[column.key as keyof T];
    if (column.render) {
      const rendered = column.render(value, row);
      if (rendered === null || rendered === undefined || rendered === '') {
        return EMPTY_PLACEHOLDER;
      }
      return rendered;
    }
    const str = String(value ?? '').trim();
    return str === '' ? EMPTY_PLACEHOLDER : str;
  };

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = totalItems === 0 ? 0 : Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className={styles.tableContainer} ref={containerRef}>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead className={clsx(styles.tableHeading, 'body-medium')} ref={headerRef}>
            <tr className={styles.tableHeadingRow}>
              {columns.map((column) => {
                const isSortable = canSortBy(column);
                return (
                  <th
                    key={String(column.key)}
                    className={styles.tableHeadingCell}
                    style={{ width: column.width }}
                    aria-sort={isSortable ? ariaSortOf(column.key) : undefined}
                  >
                    {/* A button, so the header is reachable with Tab and sorts on Enter
                        or Space the way it does on a click. */}
                    {isSortable ? (
                      <button
                        type="button"
                        className={styles.sortableHeader}
                        onClick={() => handleSort(column.key)}
                      >
                        {column.label} {renderSortIcon(column.key)}
                      </button>
                    ) : (
                      column.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className={styles.tableBody} ref={bodyRef} aria-busy={loading || undefined}>
            {loading &&
              Array.from({ length: itemsPerPage }, (_, i) => (
                <tr key={i} className={clsx(styles.tableBodyRow, styles.skeletonRow)}>
                  {columns.map((column, index) => (
                    <td key={String(column.key)} className={styles.tableBodyCell}>
                      {column.skeleton === undefined ? (
                        <Skeleton
                          variant="text"
                          width={BONE_WIDTHS[index % BONE_WIDTHS.length]}
                          height={14}
                        />
                      ) : (
                        column.skeleton
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            {!loading &&
              paginatedData.map((row) => {
                const subRow = renderSubRow?.(row);
                return (
                  <Fragment key={row.id}>
                    <tr
                      className={clsx(
                        styles.tableBodyRow,
                        { [styles.clickable]: !!onRowClick },
                        subRow && styles.hasSubRow,
                        rowClassName?.(row),
                      )}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                    >
                      {columns.map((column) => (
                        <td
                          key={String(column.key)}
                          className={clsx(
                            styles.tableBodyCell,
                            column.truncate && styles.cellTruncate,
                          )}
                        >
                          {column.truncate ? (
                            <span
                              className={styles.truncateText}
                              title={String(row[column.key as keyof T] ?? '')}
                            >
                              {getCellValue(row, column)}
                            </span>
                          ) : (
                            getCellValue(row, column)
                          )}
                        </td>
                      ))}
                    </tr>
                    {subRow && (
                      <tr className={clsx(styles.subRow, rowClassName?.(row))}>
                        <td className={styles.subRowCell} colSpan={columns.length}>
                          {subRow}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination} ref={paginationRef}>
        <div className={styles.paginationInfo}>
          {loading ? (
            <Skeleton variant="text" width={180} height={14} />
          ) : (
            `Showing ${startItem}-${endItem} of ${totalItems} entries`
          )}
        </div>

        {/* Hidden rather than dropped while loading, so the footer keeps its height and
            the row count measured against it does not change when the data lands. */}
        <div className={clsx(styles.paginationControls, loading && styles.paginationIdle)}>
          <button
            className={styles.paginationButton}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ‹
          </button>

          {getPageNumbers().map((page) => (
            <button
              key={page}
              className={clsx(styles.pageNumber, { [styles.activePage]: page === currentPage })}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}

          <button
            className={styles.paginationButton}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
